import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarCheck2,
  CheckCircle2,
  Clock3,
  Copy,
  CreditCard,
  Loader2,
  Plus,
  RefreshCw,
  Settings2,
  ShieldCheck,
  UserRound,
  UsersRound,
} from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/useAuthStore';
import {
  ROLE_KEYS,
  canManageTeamAttendance,
  normalizeRole,
} from '../utils/rolePermissions';
import {
  checkInTeamMember,
  checkOutTeamMember,
  correctTeamAttendance,
  createLeaveRequest,
  createTeamMember,
  decideLeaveRequest,
  getAttendanceSettings,
  getLeaveRequests,
  getPaymentSlips,
  getTeamMembers,
  getTeamMonthlySummary,
  getTeamToday,
  markTeamAttendance,
  regenerateTeamMemberInvite,
  updateAttendanceSettings,
  updateTeamMember,
  upsertPaymentSlip,
} from '../services/staffAttendanceApi';

const TEAM_TYPES = [
  { value: 'on_roll_doctor', label: 'On-Roll Doctor' },
  { value: 'front_desk', label: 'Front Desk' },
  { value: 'dental_assistant', label: 'Dental Assistant' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'hygienist', label: 'Hygienist' },
  { value: 'other_staff', label: 'Other Staff' },
];

const ROLE_LEVELS = [
  { value: 'owner_admin', label: 'Owner/Admin' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'staff', label: 'Staff' },
  { value: 'accountant', label: 'Accountant' },
];

const WEEKDAYS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const TABS = [
  { id: 'today', label: "Today's Attendance", icon: CalendarCheck2 },
  { id: 'members', label: 'Team Members', icon: UsersRound },
  { id: 'leave', label: 'Leave Requests', icon: Clock3 },
  { id: 'slips', label: 'Payment Slips', icon: CreditCard },
  { id: 'monthly', label: 'Monthly Summary', icon: CheckCircle2 },
  { id: 'settings', label: 'Attendance Settings', icon: Settings2 },
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function currentMonthYear() {
  const now = new Date();
  return {
    month: String(now.getMonth() + 1),
    year: String(now.getFullYear()),
  };
}

function formatDate(value) {
  if (!value) return '-';
  const parsed = new Date(`${value}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function formatMinutes(value) {
  const total = Math.max(0, Number(value || 0));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (!hours) return `${minutes}m`;
  if (!minutes) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return 'Locked';
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function labelize(value) {
  return String(value || '-')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function friendlyError(error) {
  const status = error?.response?.status;
  const message = error?.response?.data?.error || error?.message || 'Request failed.';
  if (status === 403) return message || 'This action is restricted for your role.';
  if (status === 423) return message || 'Unlock Finance Lock to view money values.';
  if (status === 400) return message || 'Please check the entered values.';
  return message;
}

function statusBadgeClasses(status) {
  const value = String(status || '').toLowerCase();
  if (value === 'present') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (value === 'missing_checkout') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (value === 'absent_suggested') return 'border-rose-200 bg-rose-50 text-rose-700';
  if (['absent', 'leave'].includes(value)) return 'border-rose-200 bg-rose-50 text-rose-700';
  if (value === 'half_day') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (value === 'holiday') return 'border-slate-200 bg-slate-100 text-slate-700';
  return 'border-slate-200 bg-white text-slate-600';
}

const emptyMemberForm = {
  id: null,
  fullName: '',
  teamType: 'front_desk',
  mobileNumber: '',
  email: '',
  joiningDate: '',
  workStatus: 'active',
  loginAccess: false,
  attendanceTracking: true,
  scheduledStartTime: '09:00',
  scheduledEndTime: '18:00',
  breakDurationMinutes: 0,
  workingDays: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
  rolePermissionLevel: '',
  notes: '',
  linkedUserId: null,
  originalLoginAccess: false,
  originalAttendanceTracking: true,
};

const emptyLeaveForm = {
  teamMemberId: '',
  leaveType: 'full_day',
  fromDate: todayKey(),
  toDate: todayKey(),
  reason: '',
};

const emptySlipForm = {
  teamMemberId: '',
  month: currentMonthYear().month,
  year: currentMonthYear().year,
  basicSalary: '',
  deductions: '',
  bonus: '',
  overtimeAmount: '',
  paidStatus: 'unpaid',
  paymentDate: '',
  paymentMode: '',
  remarks: '',
};

function ModalShell({ title, subtitle, onClose = () => {}, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-2 backdrop-blur-sm sm:p-4">
      <div className="flex max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)] sm:max-h-[calc(100dvh-2rem)]">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6 sm:py-5">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600">Team & Attendance</p>
            <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
            {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-500 transition hover:border-blue-200 hover:text-blue-700"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50 ${props.className || ''}`}
    />
  );
}

function SelectInput(props) {
  return (
    <select
      {...props}
      className={`w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50 ${props.className || ''}`}
    />
  );
}

export default function TeamAttendance() {
  const user = useAuthStore((state) => state.user);
  const roleKey = normalizeRole(user?.role);
  const canManage = canManageTeamAttendance(roleKey);
  const canCorrect = roleKey === ROLE_KEYS.OWNER_ADMIN;
  const canManagePayments = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.ACCOUNTANT].includes(roleKey);
  const canViewAttendanceOps = [ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR, ROLE_KEYS.STAFF].includes(roleKey);
  const isStaff = roleKey === ROLE_KEYS.STAFF;

  const [activeTab, setActiveTab] = useState(() => (roleKey === ROLE_KEYS.ACCOUNTANT ? 'slips' : 'today'));
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [monthYear, setMonthYear] = useState(currentMonthYear());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const savingRef = useRef('');
  const [memberSaveError, setMemberSaveError] = useState('');
  const [successNotice, setSuccessNotice] = useState('');
  const [members, setMembers] = useState([]);
  const [todayPayload, setTodayPayload] = useState({ rows: [], summary: {} });
  const [leavePayload, setLeavePayload] = useState({ rows: [] });
  const [slipPayload, setSlipPayload] = useState({ rows: [], finance: {} });
  const [monthlyPayload, setMonthlyPayload] = useState({ totals: {}, byMember: [] });
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberForm, setMemberForm] = useState(emptyMemberForm);
  const [inviteNotice, setInviteNotice] = useState(null);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState(emptyLeaveForm);
  const [slipModalOpen, setSlipModalOpen] = useState(false);
  const [slipForm, setSlipForm] = useState(emptySlipForm);
  const [correctionModal, setCorrectionModal] = useState({ open: false, row: null, reason: '', status: 'present', checkInTime: '', checkOutTime: '', breakMinutes: 0, remarks: '' });
  const [settingsForm, setSettingsForm] = useState({
    defaultScheduledStartTime: '09:00',
    defaultScheduledEndTime: '18:00',
    defaultBreakDurationMinutes: 0,
    lateGracePeriodMinutes: 10,
    autoSuggestAbsentAfterEndOfDay: true,
    requireReasonForManualCorrection: true,
  });

  const selfTeamMemberId = useMemo(() => {
    if (!isStaff) return '';
    const own = members.find((member) => Number(member.linkedUserId) === Number(user?.id || user?.userId));
    return own?.id ? String(own.id) : '';
  }, [isStaff, members, user?.id, user?.userId]);

  const loadMembers = useCallback(async () => {
    const payload = await getTeamMembers({ includeInactive: true });
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    setMembers(rows);
    if (!leaveForm.teamMemberId && rows.length > 0) {
      setLeaveForm((current) => ({ ...current, teamMemberId: String(rows[0].id) }));
    }
    if (!slipForm.teamMemberId && rows.length > 0) {
      setSlipForm((current) => ({ ...current, teamMemberId: String(rows[0].id) }));
    }
  }, [leaveForm.teamMemberId, slipForm.teamMemberId]);

  const loadToday = useCallback(async () => {
    if (!canViewAttendanceOps) {
      setTodayPayload({ rows: [], summary: {} });
      return;
    }
    const payload = await getTeamToday({ date: selectedDate });
    setTodayPayload(payload || { rows: [], summary: {} });
  }, [canViewAttendanceOps, selectedDate]);

  const loadLeaves = useCallback(async () => {
    if (!canViewAttendanceOps) {
      setLeavePayload({ rows: [] });
      return;
    }
    const payload = await getLeaveRequests();
    setLeavePayload(payload || { rows: [] });
  }, [canViewAttendanceOps]);

  const loadSlips = useCallback(async () => {
    const payload = await getPaymentSlips(monthYear);
    setSlipPayload(payload || { rows: [], finance: {} });
  }, [monthYear]);

  const loadMonthly = useCallback(async () => {
    if (!canViewAttendanceOps) {
      setMonthlyPayload({ totals: {}, byMember: [], rowCount: 0 });
      return;
    }
    const payload = await getTeamMonthlySummary(monthYear);
    setMonthlyPayload(payload || { totals: {}, byMember: [] });
  }, [canViewAttendanceOps, monthYear]);

  const loadSettings = useCallback(async () => {
    if (!canViewAttendanceOps) return;
    const payload = await getAttendanceSettings();
    setSettingsForm({
      defaultScheduledStartTime: String(payload?.defaultScheduledStartTime || '09:00').slice(0, 5),
      defaultScheduledEndTime: String(payload?.defaultScheduledEndTime || '18:00').slice(0, 5),
      defaultBreakDurationMinutes: Number(payload?.defaultBreakDurationMinutes || 0),
      lateGracePeriodMinutes: Number(payload?.lateGracePeriodMinutes || 10),
      autoSuggestAbsentAfterEndOfDay: Boolean(payload?.autoSuggestAbsentAfterEndOfDay),
      requireReasonForManualCorrection: Boolean(payload?.requireReasonForManualCorrection),
    });
  }, [canViewAttendanceOps]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadMembers(), loadToday(), loadLeaves(), loadSlips(), loadMonthly(), loadSettings()]);
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setLoading(false);
    }
  }, [loadLeaves, loadMembers, loadMonthly, loadSettings, loadSlips, loadToday]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshOperational = async () => {
    await Promise.all([loadMembers(), loadToday(), loadLeaves(), loadSlips(), loadMonthly(), loadSettings()]);
  };

  const submitMember = async () => {
    if (savingRef.current) return;
    if (!memberForm.fullName.trim()) {
      setMemberSaveError('Full name is required.');
      toast.error('Full name is required.');
      return;
    }
    if (memberForm.loginAccess && !memberForm.email.trim()) {
      setMemberSaveError('Email is required when Login Access is enabled.');
      return;
    }
    if (memberForm.loginAccess && !memberForm.rolePermissionLevel) {
      setMemberSaveError('Role / permission level is required when Login Access is enabled.');
      return;
    }
    if (memberForm.loginAccess && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberForm.email.trim())) {
      setMemberSaveError('Enter a valid email address for Login Access.');
      return;
    }
    if (memberForm.linkedUserId && memberForm.originalLoginAccess && !memberForm.loginAccess) {
      setMemberSaveError('This linked login cannot be deactivated from Team Attendance. Keep Login Access enabled and contact Dektal Support for account deactivation.');
      return;
    }
    savingRef.current = 'member';
    setSaving('member');
    setMemberSaveError('');
    try {
      const {
        linkedUserId: _linkedUserId,
        originalLoginAccess: _originalLoginAccess,
        originalAttendanceTracking: _originalAttendanceTracking,
        id: _memberId,
        ...memberPayload
      } = memberForm;
      const payload = {
        ...memberPayload,
        workingDays: memberForm.workingDays,
      };
      let savedMember;
      if (memberForm.id) {
        savedMember = await updateTeamMember(memberForm.id, payload);
        setSuccessNotice('Team member updated successfully. Login and attendance settings are now up to date.');
        toast.success('Team member updated.');
      } else {
        savedMember = await createTeamMember(payload);
        setSuccessNotice('Team member saved successfully and the attendance list is up to date.');
        toast.success('New team member added successfully!');
      }
      if (savedMember?.loginInvite?.inviteLink) {
        setInviteNotice({
          ...savedMember.loginInvite,
          teamMemberName: savedMember.fullName,
          teamMemberId: savedMember.id,
        });
        toast.success('Password setup invite created.');
      }
      await refreshOperational();
      setMemberModalOpen(false);
      setMemberForm(emptyMemberForm);
    } catch (error) {
      const message = friendlyError(error);
      setMemberSaveError(message);
      toast.error(message);
    } finally {
      savingRef.current = '';
      setSaving('');
    }
  };

  const copyInviteLink = async (invite = inviteNotice) => {
    if (!invite?.inviteLink) return;
    try {
      await navigator.clipboard.writeText(invite.inviteLink);
      toast.success('Set password link copied.');
    } catch (_error) {
      toast.error('Copy failed. Select and copy the link manually.');
    }
  };

  const regenerateInvite = async (member) => {
    if (!member?.id) return;
    setSaving(`invite-${member.id}`);
    try {
      const payload = await regenerateTeamMemberInvite(member.id);
      if (payload?.loginInvite?.inviteLink) {
        setInviteNotice({
          ...payload.loginInvite,
          teamMemberName: member.fullName,
          teamMemberId: member.id,
        });
        toast.success('Invite regenerated.');
      }
      await refreshOperational();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setSaving('');
    }
  };

  const openEditMember = (member) => {
    setMemberSaveError('');
    setMemberForm({
      id: member.id,
      fullName: member.fullName || '',
      teamType: member.teamType || 'other_staff',
      mobileNumber: member.mobileNumber || '',
      email: member.email || '',
      joiningDate: member.joiningDate || '',
      workStatus: member.workStatus || 'active',
      loginAccess: Boolean(member.loginAccess),
      attendanceTracking: Boolean(member.attendanceTracking),
      scheduledStartTime: String(member.scheduledStartTime || '09:00').slice(0, 5),
      scheduledEndTime: String(member.scheduledEndTime || '18:00').slice(0, 5),
      breakDurationMinutes: Number(member.breakDurationMinutes || 0),
      workingDays: Array.isArray(member.workingDaysJson) ? member.workingDaysJson : ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'],
      rolePermissionLevel: member.rolePermissionLevel || 'staff',
      notes: member.notes || '',
      linkedUserId: member.linkedUserId || null,
      originalLoginAccess: Boolean(member.loginAccess),
      originalAttendanceTracking: Boolean(member.attendanceTracking),
    });
    setMemberModalOpen(true);
  };

  const runAction = async (key, callback) => {
    setSaving(key);
    try {
      await callback();
      await refreshOperational();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setSaving('');
    }
  };

  const submitLeave = async () => {
    const teamMemberId = isStaff ? selfTeamMemberId : leaveForm.teamMemberId;
    if (!teamMemberId) {
      toast.error('Select a team member.');
      return;
    }
    if (!leaveForm.reason.trim()) {
      toast.error('Leave reason is required.');
      return;
    }
    await runAction('leave', async () => {
      await createLeaveRequest({ ...leaveForm, teamMemberId });
      toast.success('Leave request submitted.');
      setLeaveModalOpen(false);
      setLeaveForm(emptyLeaveForm);
    });
  };

  const submitSlip = async () => {
    if (!slipForm.teamMemberId) {
      toast.error('Select a team member.');
      return;
    }
    await runAction('slip', async () => {
      await upsertPaymentSlip(slipForm);
      toast.success('Payment slip saved.');
      setSlipModalOpen(false);
      setSlipForm(emptySlipForm);
    });
  };

  const submitSettings = async () => {
    await runAction('settings', async () => {
      await updateAttendanceSettings(settingsForm);
      toast.success('Attendance settings saved.');
    });
  };

  const openCorrection = (row) => {
    const attendance = row.attendance || {};
    setCorrectionModal({
      open: true,
      row,
      reason: '',
      status: attendance.status || row.computed?.status || 'present',
      checkInTime: attendance.checkInAt ? new Date(attendance.checkInAt).toISOString().slice(11, 16) : '',
      checkOutTime: attendance.checkOutAt ? new Date(attendance.checkOutAt).toISOString().slice(11, 16) : '',
      breakMinutes: Number(attendance.breakMinutes ?? row.computed?.breakMinutes ?? 0),
      remarks: attendance.remarks || '',
    });
  };

  const submitCorrection = async () => {
    const attendanceId = correctionModal.row?.attendance?.id;
    if (!attendanceId) {
      toast.error('Create or mark attendance before correction.');
      return;
    }
    if (!correctionModal.reason.trim()) {
      toast.error('Correction reason is required.');
      return;
    }
    await runAction('correction', async () => {
      await correctTeamAttendance(attendanceId, {
        reason: correctionModal.reason,
        updates: {
          status: correctionModal.status,
          checkInTime: correctionModal.checkInTime,
          checkOutTime: correctionModal.checkOutTime,
          breakMinutes: correctionModal.breakMinutes,
          remarks: correctionModal.remarks,
        },
      });
      setCorrectionModal({ open: false, row: null, reason: '', status: 'present', checkInTime: '', checkOutTime: '', breakMinutes: 0, remarks: '' });
      toast.success('Attendance correction saved.');
    });
  };

  const summaryCards = [
    { label: 'Present Today', value: todayPayload?.summary?.present || 0, tone: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
    { label: 'Absent / Leave', value: todayPayload?.summary?.absentOrLeave || 0, tone: 'text-rose-700 bg-rose-50 border-rose-100' },
    { label: 'Missing Checkout', value: todayPayload?.summary?.missingCheckout || 0, tone: 'text-amber-700 bg-amber-50 border-amber-100' },
    { label: 'Pending Leave', value: (leavePayload.rows || []).filter((row) => row.status === 'pending').length, tone: 'text-blue-700 bg-blue-50 border-blue-100' },
  ];
  const visibleTabs = canViewAttendanceOps
    ? TABS
    : TABS.filter((tab) => ['members', 'slips'].includes(tab.id));

  if (![ROLE_KEYS.OWNER_ADMIN, ROLE_KEYS.DOCTOR, ROLE_KEYS.STAFF, ROLE_KEYS.ACCOUNTANT].includes(roleKey)) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] px-4 py-6 md:px-8">
        <section className="rounded-[26px] border border-rose-200 bg-white p-6 shadow-[0_12px_30px_rgba(15,23,42,0.05)]">
          <h1 className="text-2xl font-semibold text-slate-900">Team & Attendance</h1>
          <p className="mt-2 text-sm text-rose-600">Your role does not have attendance access.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="team-attendance-feedback min-h-screen bg-[#F8FAFC] px-4 py-6 md:px-8">
      <div className="mx-auto max-w-[1480px] space-y-5">
        <section className="overflow-hidden rounded-[30px] border border-blue-100 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.06)]">
          <div className="bg-[linear-gradient(135deg,rgba(37,99,235,0.12),rgba(255,255,255,0.92),rgba(226,232,240,0.55))] px-6 py-6 md:px-8">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-600">Clinic Team</p>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">Team & Attendance</h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                  Manage staff, on-roll doctors, attendance, leave, work timings, and payment slips.
                </p>
              </div>

              <div className="flex flex-wrap gap-2.5">
                {canManage ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMemberForm(emptyMemberForm);
                      setMemberSaveError('');
                      setMemberModalOpen(true);
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_22px_rgba(37,99,235,0.22)] transition hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add Team Member
                  </button>
                ) : null}
                <button type="button" onClick={() => setActiveTab('today')} className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700">
                  Today's Attendance
                </button>
                <button type="button" onClick={() => setActiveTab('leave')} className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700">
                  Leave Requests
                </button>
                <button type="button" onClick={() => setActiveTab('slips')} className="rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700">
                  Payment Slips
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card) => (
            <div key={card.label} className={`rounded-[24px] border bg-white p-4 shadow-[0_10px_24px_rgba(15,23,42,0.04)] ${card.tone}`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] opacity-80">{card.label}</p>
              <p className="mt-2 text-3xl font-semibold">{card.value}</p>
            </div>
          ))}
        </section>

        <section className="rounded-[28px] border border-slate-200 bg-white p-3 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
          <div className="flex flex-wrap gap-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-[0_10px_20px_rgba(37,99,235,0.18)]'
                    : 'border border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {successNotice ? (
          <div role="status" aria-live="polite" className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
            {successNotice}
          </div>
        ) : null}

        {loading ? (
          <div className="rounded-[28px] border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-blue-600" />
            Loading Team & Attendance...
          </div>
        ) : null}

        {!loading && activeTab === 'today' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Today's Attendance</h2>
                <p className="mt-1 text-sm text-slate-500">Active team members with attendance tracking enabled.</p>
              </div>
              <Field label="Date" className="w-full lg:w-56">
                <TextInput type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} />
              </Field>
            </div>

            <div className="divide-y divide-slate-100">
              {(todayPayload.rows || []).length === 0 ? (
                <div className="p-10 text-center text-sm text-slate-500">No active tracked team members yet.</div>
              ) : (
                todayPayload.rows.map((row) => {
                  const member = row.teamMember || {};
                  const attendance = row.attendance || {};
                  const computed = row.computed || {};
                  const status = attendance.status || computed.status || 'not_marked';
                  return (
                    <div key={member.id} className="space-y-4 px-5 py-5 sm:px-6">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                            <UserRound className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">{member.fullName}</p>
                            <p className="mt-1 text-sm text-slate-500">{labelize(member.teamType)} | {labelize(member.rolePermissionLevel)}</p>
                            <span className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClasses(status)}`}>
                              {labelize(status)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 min-[520px]:grid-cols-2 xl:grid-cols-4">
                        <div className="min-h-[78px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Scheduled</p>
                          <p className="mt-2 text-sm font-semibold leading-5 text-slate-800">{formatDateTime(computed.scheduledStartAt)} - {formatDateTime(computed.scheduledEndAt)}</p>
                        </div>
                        <div className="min-h-[78px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Check-in</p>
                          <p className="mt-2 text-sm font-semibold leading-5 text-slate-800">{formatDateTime(attendance.checkInAt)}</p>
                        </div>
                        <div className="min-h-[78px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Checkout</p>
                          <p className="mt-2 text-sm font-semibold leading-5 text-slate-800">{formatDateTime(attendance.checkOutAt)}</p>
                        </div>
                        <div className="min-h-[78px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Late / OT</p>
                          <p className="mt-2 text-sm font-semibold leading-5 text-slate-800">{formatMinutes(attendance.lateMinutes)} / {formatMinutes(attendance.overtimeMinutes)}</p>
                        </div>
                      </div>

                      <div className="rounded-[22px] border border-slate-100 bg-white/80 p-3">
                        <div className="flex flex-wrap gap-2">
                          <button type="button" disabled={Boolean(saving)} onClick={() => runAction(`checkin-${member.id}`, async () => checkInTeamMember(member.id, { attendanceDate: selectedDate }))} className="min-h-[38px] rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-[0_10px_20px_-18px_rgba(37,99,235,0.8)] transition hover:bg-blue-700 disabled:opacity-60">
                            Check In
                          </button>
                          <button type="button" disabled={Boolean(saving)} onClick={() => runAction(`checkout-${member.id}`, async () => checkOutTeamMember(member.id, { attendanceDate: selectedDate }))} className="min-h-[38px] rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-60">
                            Check Out
                          </button>
                          {canManage ? (
                            <>
                              {['present', 'absent', 'leave', 'half_day'].map((nextStatus) => (
                                <button key={nextStatus} type="button" disabled={Boolean(saving)} onClick={() => runAction(`${nextStatus}-${member.id}`, async () => markTeamAttendance(member.id, { attendanceDate: selectedDate, status: nextStatus, reason: `Marked ${nextStatus}` }))} className="min-h-[38px] rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700 disabled:opacity-60">
                                  {labelize(nextStatus)}
                                </button>
                              ))}
                            </>
                          ) : null}
                          {canCorrect ? (
                            <button type="button" onClick={() => openCorrection(row)} className="min-h-[38px] rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 transition hover:border-blue-200 hover:text-blue-700">
                              Manual Correction
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        ) : null}

        {!loading && activeTab === 'members' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Team Members</h2>
                <p className="mt-1 text-sm text-slate-500">Internal staff and on-roll doctors for this clinic only.</p>
              </div>
              {canManage ? (
                <button type="button" onClick={() => { setMemberForm(emptyMemberForm); setMemberSaveError(''); setMemberModalOpen(true); }} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">
                  <Plus className="h-4 w-4" /> Add Team Member
                </button>
              ) : null}
            </div>

            {canManage && inviteNotice?.inviteLink ? (
              <div className="mt-5 rounded-[22px] border border-emerald-200 bg-emerald-50 p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-emerald-900">
                      Invite created for {inviteNotice.teamMemberName || inviteNotice.staffEmail}
                    </p>
                    <p className="mt-1 text-sm text-emerald-800">
                      Share this link only with this staff member. It expires in 72 hours.
                    </p>
                    <p className="mt-2 break-all rounded-2xl border border-emerald-200 bg-white px-3 py-2 text-xs text-emerald-900">
                      {inviteNotice.inviteLink}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyInviteLink(inviteNotice)}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white"
                  >
                    <Copy className="h-4 w-4" /> Copy Set Password Link
                  </button>
                </div>
              </div>
            ) : null}

            <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
              {members.map((member) => (
                <div key={member.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-900">{member.fullName}</p>
                      <p className="mt-1 text-sm text-slate-500">{labelize(member.teamType)} | {labelize(member.workStatus)}</p>
                    </div>
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${member.workStatus === 'active' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}>
                      {labelize(member.workStatus)}
                    </span>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2" aria-label={`${member.fullName} access and attendance status`}>
                    <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${member.loginAccess ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                      Login: {member.loginAccess ? 'Enabled' : 'Disabled'}
                    </span>
                    <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${member.attendanceTracking ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600'}`}>
                      Attendance: {member.attendanceTracking ? 'Tracked' : 'Not tracked'}
                    </span>
                    {!member.loginAccess ? (
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">
                        Account invitation: Not required
                      </span>
                    ) : Number(inviteNotice?.teamMemberId) === Number(member.id) ? (
                      <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                        Account invitation: Pending
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-slate-600">
                    <p>Mobile: <span className="font-semibold text-slate-800">{member.mobileNumber || '-'}</span></p>
                    <p>Email: <span className="font-semibold text-slate-800">{member.email || '-'}</span></p>
                    <p>Shift: <span className="font-semibold text-slate-800">{String(member.scheduledStartTime || '-').slice(0, 5)} - {String(member.scheduledEndTime || '-').slice(0, 5)}</span></p>
                    <p>Break: <span className="font-semibold text-slate-800">{formatMinutes(member.breakDurationMinutes)}</span></p>
                    <p>User Link: <span className="font-semibold text-slate-800">{member.linkedUserId ? 'Linked' : 'Not linked'}</span></p>
                  </div>
                  {canManage ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => openEditMember(member)} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-200 hover:text-blue-700">
                        Edit
                      </button>
                      {member.loginAccess && member.email ? (
                        <button
                          type="button"
                          onClick={() => regenerateInvite(member)}
                          disabled={saving === `invite-${member.id}`}
                          className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-50 disabled:opacity-60"
                        >
                          <RefreshCw className="h-4 w-4" /> {saving === `invite-${member.id}` ? 'Regenerating...' : 'Regenerate Invite'}
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && activeTab === 'leave' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Leave Requests</h2>
                <p className="mt-1 text-sm text-slate-500">Pending requests stay at the top for quick owner/admin review.</p>
              </div>
              <button type="button" onClick={() => setLeaveModalOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">
                <Plus className="h-4 w-4" /> Apply Leave
              </button>
            </div>

            <div className="mt-5 space-y-3">
              {(leavePayload.rows || []).length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">No leave requests yet.</div>
              ) : (
                leavePayload.rows.map((row) => (
                  <div key={row.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">{row.teamMember?.fullName || 'Team member'}</p>
                        <p className="mt-1 text-sm text-slate-500">{labelize(row.leaveType)} | {formatDate(row.fromDate)} to {formatDate(row.toDate)}</p>
                        <p className="mt-2 text-sm text-slate-600">{row.reason}</p>
                        {row.adminRemark ? <p className="mt-2 text-xs text-slate-500">Admin remark: {row.adminRemark}</p> : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusBadgeClasses(row.status)}`}>{labelize(row.status)}</span>
                        {canManage && row.status === 'pending' ? (
                          <>
                            <button type="button" onClick={() => runAction(`approve-${row.id}`, async () => decideLeaveRequest(row.id, { decision: 'approved', adminRemark: 'Approved' }))} className="rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white">Approve</button>
                            <button type="button" onClick={() => runAction(`reject-${row.id}`, async () => decideLeaveRequest(row.id, { decision: 'rejected', adminRemark: 'Rejected' }))} className="rounded-full border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700">Reject</button>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {!loading && activeTab === 'slips' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">{isStaff ? 'My Payment Slips' : 'Payment Slips'}</h2>
                <p className="mt-1 text-sm text-slate-500">Salary values are protected by Finance Lock and role rules.</p>
              </div>
              {canManagePayments ? (
                <button type="button" onClick={() => setSlipModalOpen(true)} className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white">
                  <Plus className="h-4 w-4" /> Generate Payment Slip
                </button>
              ) : null}
            </div>

            {slipPayload?.finance?.requiresFinanceUnlock ? (
              <div className="mt-5 rounded-[22px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Unlock Finance Lock to view salary/payment values.
              </div>
            ) : null}

            <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
              {(slipPayload.rows || []).length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500 lg:col-span-2">No payment slips found for the selected month.</div>
              ) : (
                slipPayload.rows.map((row) => (
                  <div key={row.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-900">{row.teamMember?.fullName || 'Team member'}</p>
                        <p className="mt-1 text-sm text-slate-500">{row.month}/{row.year} | {labelize(row.paidStatus)}</p>
                      </div>
                      <ShieldCheck className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Basic</p>
                        <p className="mt-1 font-semibold text-slate-900">{formatMoney(row.basicSalary)}</p>
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Net Pay</p>
                        <p className="mt-1 font-semibold text-slate-900">{formatMoney(row.netPay)}</p>
                      </div>
                    </div>
                    {row.moneyRedacted ? <p className="mt-3 text-xs font-semibold text-amber-700">{row.redactionMessage}</p> : null}
                  </div>
                ))
              )}
            </div>
          </section>
        ) : null}

        {!loading && activeTab === 'monthly' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-slate-900">Monthly Summary</h2>
                <p className="mt-1 text-sm text-slate-500">Attendance and work timing totals for monthly reports.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Month"><SelectInput value={monthYear.month} onChange={(event) => setMonthYear((current) => ({ ...current, month: event.target.value }))}>{Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={String(i + 1)}>{i + 1}</option>)}</SelectInput></Field>
                <Field label="Year"><TextInput type="number" value={monthYear.year} onChange={(event) => setMonthYear((current) => ({ ...current, year: event.target.value }))} /></Field>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                ['Present', monthlyPayload?.totals?.presentDays || 0],
                ['Absent', monthlyPayload?.totals?.absentDays || 0],
                ['Leave', monthlyPayload?.totals?.leaveDays || 0],
                ['Missing Checkout', monthlyPayload?.totals?.missingCheckoutCount || 0],
                ['Late Days', monthlyPayload?.totals?.lateDays || 0],
                ['Work Hours', formatMinutes(monthlyPayload?.totals?.totalWorkMinutes || 0)],
                ['Overtime', formatMinutes(monthlyPayload?.totals?.overtimeMinutes || 0)],
                ['Rows', monthlyPayload?.rowCount || 0],
              ].map(([label, value]) => (
                <div key={label} className="rounded-[22px] border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</p>
                  <p className="mt-2 text-xl font-semibold text-slate-900">{value}</p>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && activeTab === 'settings' ? (
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_12px_28px_rgba(15,23,42,0.04)]">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900">Attendance Settings</h2>
              <p className="mt-1 text-sm text-slate-500">Defaults used when a team member does not have their own shift timing.</p>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field label="Default Start"><TextInput type="time" value={settingsForm.defaultScheduledStartTime} onChange={(event) => setSettingsForm((current) => ({ ...current, defaultScheduledStartTime: event.target.value }))} disabled={!canCorrect} /></Field>
              <Field label="Default End"><TextInput type="time" value={settingsForm.defaultScheduledEndTime} onChange={(event) => setSettingsForm((current) => ({ ...current, defaultScheduledEndTime: event.target.value }))} disabled={!canCorrect} /></Field>
              <Field label="Default Break (min)"><TextInput type="number" min="0" value={settingsForm.defaultBreakDurationMinutes} onChange={(event) => setSettingsForm((current) => ({ ...current, defaultBreakDurationMinutes: event.target.value }))} disabled={!canCorrect} /></Field>
              <Field label="Late Grace (min)"><TextInput type="number" min="0" value={settingsForm.lateGracePeriodMinutes} onChange={(event) => setSettingsForm((current) => ({ ...current, lateGracePeriodMinutes: event.target.value }))} disabled={!canCorrect} /></Field>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={settingsForm.autoSuggestAbsentAfterEndOfDay} onChange={(event) => setSettingsForm((current) => ({ ...current, autoSuggestAbsentAfterEndOfDay: event.target.checked }))} disabled={!canCorrect} />
                Auto-suggest absent after end of day
              </label>
              <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={settingsForm.requireReasonForManualCorrection} onChange={(event) => setSettingsForm((current) => ({ ...current, requireReasonForManualCorrection: event.target.checked }))} disabled={!canCorrect} />
                Require reason for correction
              </label>
            </div>
            {canCorrect ? (
              <button type="button" onClick={submitSettings} disabled={saving === 'settings'} className="mt-5 rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {saving === 'settings' ? 'Saving...' : 'Save Settings'}
              </button>
            ) : (
              <div className="mt-5 rounded-[20px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                Only Owner/Admin can edit attendance settings.
              </div>
            )}
          </section>
        ) : null}
      </div>

      {memberModalOpen ? (
        <ModalShell
          title={memberForm.id ? 'Edit Team Member' : 'Add Team Member'}
          subtitle="Salary is intentionally not required here. Payment slips can be added later."
          onClose={() => setMemberModalOpen(false)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Full Name *"><TextInput value={memberForm.fullName} onChange={(event) => setMemberForm((current) => ({ ...current, fullName: event.target.value }))} /></Field>
            <Field label="Team Type *"><SelectInput value={memberForm.teamType} onChange={(event) => setMemberForm((current) => ({ ...current, teamType: event.target.value }))}>{TEAM_TYPES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</SelectInput></Field>
            <Field label="Mobile Number"><TextInput value={memberForm.mobileNumber} onChange={(event) => setMemberForm((current) => ({ ...current, mobileNumber: event.target.value }))} /></Field>
            <Field label={memberForm.loginAccess ? 'Email *' : 'Email'}><TextInput type="email" value={memberForm.email} onChange={(event) => { setMemberForm((current) => ({ ...current, email: event.target.value })); setMemberSaveError(''); }} /></Field>
            <Field label="Joining Date"><TextInput type="date" value={memberForm.joiningDate} onChange={(event) => setMemberForm((current) => ({ ...current, joiningDate: event.target.value }))} /></Field>
            <Field label="Work Status"><SelectInput value={memberForm.workStatus} onChange={(event) => setMemberForm((current) => ({ ...current, workStatus: event.target.value }))}><option value="active">Active</option><option value="inactive">Inactive</option></SelectInput></Field>
            <Field label="Scheduled Start"><TextInput type="time" value={memberForm.scheduledStartTime} onChange={(event) => setMemberForm((current) => ({ ...current, scheduledStartTime: event.target.value }))} /></Field>
            <Field label="Scheduled End"><TextInput type="time" value={memberForm.scheduledEndTime} onChange={(event) => setMemberForm((current) => ({ ...current, scheduledEndTime: event.target.value }))} /></Field>
            <Field label="Break Duration (min)"><TextInput type="number" min="0" value={memberForm.breakDurationMinutes} onChange={(event) => setMemberForm((current) => ({ ...current, breakDurationMinutes: event.target.value }))} /></Field>
            <Field label={memberForm.loginAccess ? 'Role / Permission Level *' : 'Role / Permission Level'}><SelectInput value={memberForm.rolePermissionLevel} onChange={(event) => { setMemberForm((current) => ({ ...current, rolePermissionLevel: event.target.value })); setMemberSaveError(''); }}><option value="">Select role</option>{ROLE_LEVELS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</SelectInput></Field>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className={`flex min-h-[112px] cursor-pointer items-start gap-3 rounded-2xl border px-4 py-4 ${memberForm.loginAccess ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200 bg-slate-50'} ${memberForm.linkedUserId && memberForm.originalLoginAccess ? 'cursor-not-allowed opacity-80' : ''}`}>
              <input aria-label="Login Access" className="mt-1 h-5 w-5 shrink-0 accent-indigo-600" type="checkbox" checked={memberForm.loginAccess} disabled={Boolean(memberForm.linkedUserId && memberForm.originalLoginAccess)} onChange={(event) => { setMemberForm((current) => ({ ...current, loginAccess: event.target.checked })); setMemberSaveError(''); }} />
              <span><span className="block text-sm font-semibold text-slate-800">Login Access</span><span className="mt-1.5 block text-xs font-normal leading-5 text-slate-600">Allow this team member to log in to Dektal 360 Pro. Their access will be limited by the selected role and permissions.</span></span>
            </label>
            <label className={`flex min-h-[112px] cursor-pointer items-start gap-3 rounded-2xl border px-4 py-4 ${memberForm.attendanceTracking ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
              <input aria-label="Attendance Tracking" className="mt-1 h-5 w-5 shrink-0 accent-blue-600" type="checkbox" checked={memberForm.attendanceTracking} onChange={(event) => { setMemberForm((current) => ({ ...current, attendanceTracking: event.target.checked })); setMemberSaveError(''); }} />
              <span><span className="block text-sm font-semibold text-slate-800">Attendance Tracking</span><span className="mt-1.5 block text-xs font-normal leading-5 text-slate-600">Include this team member in attendance records. This does not give them permission to log in.</span></span>
            </label>
          </div>
          {memberForm.loginAccess ? <p className="mt-3 rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs leading-5 text-indigo-800">After saving, the existing secure invitation and set-password link will be generated for this email.</p> : null}
          {memberForm.linkedUserId && memberForm.originalLoginAccess ? <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">This member already has a linked login. Account deactivation is not available from Team Attendance, so Login Access cannot be switched off here. Attendance Tracking remains independent.</p> : null}
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Working Days</p>
            {memberForm.attendanceTracking ? <><p className="mt-1 text-xs text-slate-500">Choose the days this member is expected in daily attendance.</p><div className="mt-2 flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => {
                const selected = memberForm.workingDays.includes(day.value);
                return (
                  <button key={day.value} type="button" onClick={() => setMemberForm((current) => ({ ...current, workingDays: selected ? current.workingDays.filter((item) => item !== day.value) : [...current.workingDays, day.value] }))} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${selected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-500'}`}>
                    {day.label}
                  </button>
                );
              })}
            </div></> : <p className="mt-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">Attendance is not tracked for this member. Existing attendance history is preserved.</p>}
          </div>
          <Field label="Notes" className="mt-4"><textarea className="min-h-[96px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50" value={memberForm.notes} onChange={(event) => setMemberForm((current) => ({ ...current, notes: event.target.value }))} /></Field>
          {memberSaveError ? <div role="alert" className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{memberSaveError}</div> : null}
          <div data-testid="team-member-modal-actions" className="sticky bottom-0 z-10 -mx-5 mt-6 flex justify-end gap-2 border-t border-slate-100 bg-white px-5 pb-[env(safe-area-inset-bottom)] pt-4 shadow-[0_-10px_24px_rgba(255,255,255,0.96)] sm:-mx-6 sm:px-6">
            <button type="button" onClick={() => setMemberModalOpen(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="button" onClick={submitMember} disabled={saving === 'member'} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{saving === 'member' ? 'Saving...' : 'Save Team Member'}</button>
          </div>
        </ModalShell>
      ) : null}

      {leaveModalOpen ? (
        <ModalShell
          title="Apply Leave"
          subtitle="Staff can submit their own leave. Owner/Admin can review and approve."
          onClose={() => setLeaveModalOpen(false)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {!isStaff ? <Field label="Team Member"><SelectInput value={leaveForm.teamMemberId} onChange={(event) => setLeaveForm((current) => ({ ...current, teamMemberId: event.target.value }))}>{members.map((member) => <option key={member.id} value={member.id}>{member.fullName}</option>)}</SelectInput></Field> : null}
            <Field label="Leave Type"><SelectInput value={leaveForm.leaveType} onChange={(event) => setLeaveForm((current) => ({ ...current, leaveType: event.target.value }))}><option value="full_day">Full Day</option><option value="half_day">Half Day</option><option value="sick_leave">Sick Leave</option><option value="casual_leave">Casual Leave</option><option value="emergency_leave">Emergency Leave</option><option value="other">Other</option></SelectInput></Field>
            <Field label="From Date"><TextInput type="date" value={leaveForm.fromDate} onChange={(event) => setLeaveForm((current) => ({ ...current, fromDate: event.target.value }))} /></Field>
            <Field label="To Date"><TextInput type="date" value={leaveForm.toDate} onChange={(event) => setLeaveForm((current) => ({ ...current, toDate: event.target.value }))} /></Field>
          </div>
          <Field label="Reason *" className="mt-4"><textarea className="min-h-[110px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50" value={leaveForm.reason} onChange={(event) => setLeaveForm((current) => ({ ...current, reason: event.target.value }))} /></Field>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setLeaveModalOpen(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="button" onClick={submitLeave} disabled={saving === 'leave'} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Submit Leave</button>
          </div>
        </ModalShell>
      ) : null}

      {slipModalOpen ? (
        <ModalShell
          title="Generate Payment Slip"
          subtitle="Payment values are finance-sensitive and remain protected by Finance Lock."
          onClose={() => setSlipModalOpen(false)}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Team Member"><SelectInput value={slipForm.teamMemberId} onChange={(event) => setSlipForm((current) => ({ ...current, teamMemberId: event.target.value }))}>{members.map((member) => <option key={member.id} value={member.id}>{member.fullName}</option>)}</SelectInput></Field>
            <Field label="Paid Status"><SelectInput value={slipForm.paidStatus} onChange={(event) => setSlipForm((current) => ({ ...current, paidStatus: event.target.value }))}><option value="unpaid">Unpaid</option><option value="paid">Paid</option></SelectInput></Field>
            <Field label="Month"><TextInput type="number" min="1" max="12" value={slipForm.month} onChange={(event) => setSlipForm((current) => ({ ...current, month: event.target.value }))} /></Field>
            <Field label="Year"><TextInput type="number" min="2000" value={slipForm.year} onChange={(event) => setSlipForm((current) => ({ ...current, year: event.target.value }))} /></Field>
            <Field label="Basic Salary / Fixed Amount"><TextInput type="number" min="0" value={slipForm.basicSalary} onChange={(event) => setSlipForm((current) => ({ ...current, basicSalary: event.target.value }))} /></Field>
            <Field label="Deductions"><TextInput type="number" min="0" value={slipForm.deductions} onChange={(event) => setSlipForm((current) => ({ ...current, deductions: event.target.value }))} /></Field>
            <Field label="Bonus / Incentive"><TextInput type="number" min="0" value={slipForm.bonus} onChange={(event) => setSlipForm((current) => ({ ...current, bonus: event.target.value }))} /></Field>
            <Field label="Overtime Amount"><TextInput type="number" min="0" value={slipForm.overtimeAmount} onChange={(event) => setSlipForm((current) => ({ ...current, overtimeAmount: event.target.value }))} /></Field>
            <Field label="Payment Date"><TextInput type="date" value={slipForm.paymentDate} onChange={(event) => setSlipForm((current) => ({ ...current, paymentDate: event.target.value }))} /></Field>
            <Field label="Payment Mode"><TextInput value={slipForm.paymentMode} onChange={(event) => setSlipForm((current) => ({ ...current, paymentMode: event.target.value }))} /></Field>
          </div>
          <Field label="Remarks" className="mt-4"><textarea className="min-h-[96px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50" value={slipForm.remarks} onChange={(event) => setSlipForm((current) => ({ ...current, remarks: event.target.value }))} /></Field>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setSlipModalOpen(false)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="button" onClick={submitSlip} disabled={saving === 'slip'} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Save Slip</button>
          </div>
        </ModalShell>
      ) : null}

      {correctionModal.open ? (
        <ModalShell
          title="Manual Attendance Correction"
          subtitle="Owner/Admin corrections require a clear reason and are audit logged."
          onClose={() => setCorrectionModal((current) => ({ ...current, open: false }))}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Status"><SelectInput value={correctionModal.status} onChange={(event) => setCorrectionModal((current) => ({ ...current, status: event.target.value }))}><option value="present">Present</option><option value="absent">Absent</option><option value="leave">Leave</option><option value="half_day">Half Day</option><option value="holiday">Holiday</option></SelectInput></Field>
            <Field label="Break Minutes"><TextInput type="number" min="0" value={correctionModal.breakMinutes} onChange={(event) => setCorrectionModal((current) => ({ ...current, breakMinutes: event.target.value }))} /></Field>
            <Field label="Check-in Time"><TextInput type="time" value={correctionModal.checkInTime} onChange={(event) => setCorrectionModal((current) => ({ ...current, checkInTime: event.target.value }))} /></Field>
            <Field label="Checkout Time"><TextInput type="time" value={correctionModal.checkOutTime} onChange={(event) => setCorrectionModal((current) => ({ ...current, checkOutTime: event.target.value }))} /></Field>
          </div>
          <Field label="Reason *" className="mt-4"><textarea className="min-h-[90px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50" value={correctionModal.reason} onChange={(event) => setCorrectionModal((current) => ({ ...current, reason: event.target.value }))} /></Field>
          <Field label="Remarks" className="mt-4"><textarea className="min-h-[80px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-50" value={correctionModal.remarks} onChange={(event) => setCorrectionModal((current) => ({ ...current, remarks: event.target.value }))} /></Field>
          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={() => setCorrectionModal((current) => ({ ...current, open: false }))} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600">Cancel</button>
            <button type="button" onClick={submitCorrection} disabled={saving === 'correction'} className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">Save Correction</button>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}
